import React, { useState } from 'react';
import { Box, Container, Typography, Paper, Grid } from '@mui/material';
import { motion } from 'framer-motion';
import { MenuBook, Gavel, Forum, Link as LinkIcon, GppBad } from '@mui/icons-material';

const policySections = [
  {
    id: "introduction",
    number: "1",
    icon: <MenuBook />,
    title: "Introduction",
    content: "Welcome to AR Infotech. These Terms and Conditions outline the rules and regulations for the use of our Website and Services. By accessing this website, we assume you accept these terms and conditions. Do not continue to use AR Infotech if you do not agree to take all of the terms and conditions stated on this page."
  },
  {
    id: "license",
    number: "2",
    icon: <Gavel />,
    title: "Software License",
    content: "Unless otherwise stated, AR Infotech and/or its licensors own the intellectual property rights for all material on AR Infotech. All intellectual property rights are reserved. You may access this from AR Infotech for your own personal use subjected to restrictions set in these terms and conditions. You must not republish, sell, rent, or sub-license material from AR Infotech."
  },
  {
    id: "user-comments",
    number: "3",
    icon: <Forum />,
    title: "User Comments",
    content: "Certain parts of this website offer the opportunity for users to post and exchange opinions and information in certain areas of the website. AR Infotech does not filter, edit, publish or review Comments prior to their presence on the website. Comments do not reflect the views and opinions of AR Infotech, its agents and/or affiliates."
  },
  {
    id: "hyperlinking",
    number: "4",
    icon: <LinkIcon />,
    title: "Hyperlinking to our Content",
    content: "The following organizations may link to our Website without prior written approval: Government agencies, search engines, news organizations, and online directory distributors. These organizations may link to our home page, to publications or to other Website information so long as the link is not deceptive and does not falsely imply sponsorship."
  },
  {
    id: "liability",
    number: "5",
    icon: <GppBad />,
    title: "Content Liability",
    content: "We shall not be hold responsible for any content that appears on your Website. You agree to protect and defend us against all claims that is rising on your Website. No link(s) should appear on any Website that may be interpreted as libelous, obscene or criminal, or which infringes, otherwise violates, or advocates the infringement or other violation of, any third party rights."
  }
];

const TermsConditions = () => {
  const [activeSection, setActiveSection] = useState(policySections[0].id);

  const scrollToSection = (id) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -115;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#F1F5F9', pb: 10 }}>
      {/* Banner Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0F172A 0%, #312E81 100%)', // Indigo touch
          color: '#FFF',
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
          textAlign: 'center'
        }}
      >
        {/* Geometric Background Elements */}
        <motion.div
          animate={{ x: [0, 50, 0], y: [0, 30, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          style={{
            position: 'absolute',
            top: '10%',
            right: '10%',
            width: '300px',
            height: '300px',
            border: '2px solid rgba(255,255,255,0.05)',
            transform: 'rotate(45deg)',
            pointerEvents: 'none'
          }}
        />
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.6 }}>
            <Typography variant="h2" sx={{ fontWeight: 800, mb: 2, fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
              Terms & Conditions
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.8, fontWeight: 400 }}>
              The rules, guidelines, and terms governing your use of our platform.
            </Typography>
          </motion.div>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt: { xs: 3, md: 5 }, position: 'relative', zIndex: 2 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={4} lg={3}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                boxShadow: '0 10px 30px -10px rgba(0,0,0,0.05)',
                position: { md: 'sticky' },
                top: { md: 100 },
                backgroundColor: 'rgba(255,255,255,0.9)',
                backdropFilter: 'blur(10px)'
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1E293B' }}>
                Table of Contents
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {policySections.map((section) => (
                  <Box
                    key={`toc-${section.id}`}
                    onClick={() => scrollToSection(section.id)}
                    sx={{
                      cursor: 'pointer',
                      py: 1,
                      px: 2,
                      borderRadius: 1,
                      transition: 'all 0.2s',
                      backgroundColor: activeSection === section.id ? 'rgba(49, 46, 129, 0.08)' : 'transparent',
                      color: activeSection === section.id ? '#312E81' : '#64748B',
                      borderLeft: activeSection === section.id ? '3px solid #312E81' : '3px solid transparent',
                      fontWeight: activeSection === section.id ? 600 : 400,
                      '&:hover': {
                        backgroundColor: 'rgba(49, 46, 129, 0.04)',
                        color: '#312E81'
                      }
                    }}
                  >
                    <Typography variant="body2">{section.number}. {section.title}</Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={8} lg={9}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {policySections.map((section, index) => (
                <motion.div
                  key={`content-${section.id}`}
                  initial={{ y: 50, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                    <Paper
                      id={section.id}
                      elevation={2}
                      sx={{
                        p: { xs: 3, md: 5 },
                        borderRadius: 3,
                        scrollMarginTop: { xs: '105px', md: '120px' },
                        backgroundColor: '#FFFFFF',
                        boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)',
                        border: '1px solid rgba(0,0,0,0.05)',
                        transition: 'transform 0.3s, box-shadow 0.3s',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 10px 30px -10px rgba(0,0,0,0.1)'
                        }
                      }}
                    >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                      <Box sx={{
                        backgroundColor: '#312E81',
                        color: '#FFF',
                        width: 44,
                        height: 44,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '10%',
                        fontWeight: 700,
                        fontSize: '1.25rem',
                        flexShrink: 0
                      }}>
                        {section.number}
                      </Box>
                      <Box sx={{ color: '#312E81', display: 'flex', opacity: 0.8 }}>
                        {section.icon}
                      </Box>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: '#0F172A' }}>
                        {section.title}
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ color: '#475569', lineHeight: 1.8 }}>
                      {section.content}
                    </Typography>
                  </Paper>
                </motion.div>
              ))}
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default TermsConditions;
